import { useState, useEffect, useCallback, useRef } from "react";
import { apiPost, apiPut } from "../lib/api.js";
import FolderExplorer from "./FolderExplorer.js";
import type { EnrichedProject } from "../types/index.js";
import type { IngestionStatus } from "../hooks/useIngestionStatus.js";

interface ProjectSetupWizardProps {
	project: EnrichedProject;
	ingestionStatus: IngestionStatus | null;
	onComplete: () => void;
	onSkip: () => void;
	/** Test seam — start at a specific step. Defaults to 1. */
	initialStep?: 1 | 2 | 3;
}

/** Step metadata for the indicator. */
const STEPS = [
	{ number: 1, label: "Connect" },
	{ number: 2, label: "Documents" },
	{ number: 3, label: "Sync" },
] as const;

/**
 * Project Setup Wizard — a 3-step guided onboarding flow that appears
 * inline in the chat area when a user selects a project with no linked
 * WorkDrive folder.
 *
 * Steps:
 *   1. Connect — syncs tasks & milestones from Zoho Projects
 *   2. Documents — browse and link a WorkDrive folder
 *   3. Sync — shows document ingestion progress
 */
export default function ProjectSetupWizard({
	project,
	ingestionStatus,
	onComplete,
	onSkip,
	initialStep = 1,
}: ProjectSetupWizardProps) {
	const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(initialStep);

	/* ------------------------------------------------------------------ */
	/*  Step 1 state                                                        */
	/* ------------------------------------------------------------------ */
	const [syncing, setSyncing] = useState(false);
	const [syncError, setSyncError] = useState<string | null>(null);
	const [syncResult, setSyncResult] = useState<{ tasks: number; milestones: number } | null>(null);

	/* ------------------------------------------------------------------ */
	/*  Step 2 state                                                        */
	/* ------------------------------------------------------------------ */
	const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
	const [selectedFolderName, setSelectedFolderName] = useState<string | null>(null);
	const [linking, setLinking] = useState(false);
	const [linkError, setLinkError] = useState<string | null>(null);

	/* ------------------------------------------------------------------ */
	/*  Ref to prevent double auto-close in Step 3                          */
	/* ------------------------------------------------------------------ */
	const autoClosedRef = useRef(false);

	/* ------------------------------------------------------------------ */
	/*  Step 1: sync entities on mount                                      */
	/* ------------------------------------------------------------------ */
	const syncEntities = useCallback(async () => {
		setSyncing(true);
		setSyncError(null);
		setSyncResult(null);

		try {
			const response = await apiPost(
				`/api/projects/${project.id}/sync-entities`,
				{},
			);

			if (!response.ok) {
				let message = "Something went wrong whilst syncing project data.";
				try {
					const errorData = await response.json() as { message?: string };
					if (errorData.message) {
						message = errorData.message;
					}
				} catch {
					/* JSON parse failed — use generic message */
				}
				setSyncError(message);
				return;
			}

			const data = await response.json() as { tasks: number; milestones: number };
			setSyncResult(data);
		} catch {
			setSyncError("Failed to connect to the server. Please check your connection and try again.");
		} finally {
			setSyncing(false);
		}
	}, [project.id]);

	useEffect(() => {
		if (initialStep === 1) {
			void syncEntities();
		}
		// Only run on mount (or when project changes via key)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* Auto-advance from Step 1 → Step 2 once sync succeeds.
	   Uses requestAnimationFrame so the success state (counts) renders
	   visibly in the DOM before the step transition occurs. */
	useEffect(() => {
		if (currentStep !== 1 || !syncResult) return;
		const raf = requestAnimationFrame(() => {
			setCurrentStep(2);
		});
		return () => cancelAnimationFrame(raf);
	}, [currentStep, syncResult]);

	/* ------------------------------------------------------------------ */
	/*  Step 2: folder selection                                            */
	/* ------------------------------------------------------------------ */
	function handleFolderSelect(folderId: string, folderName: string): void {
		setSelectedFolderId(folderId);
		setSelectedFolderName(folderName);
		setLinkError(null);
	}

	async function handleLinkFolder(): Promise<void> {
		if (!selectedFolderId || !selectedFolderName) return;

		setLinking(true);
		setLinkError(null);

		try {
			/* Step A: link the folder */
			const putResponse = await apiPut(
				`/api/projects/${project.id}/folder`,
				{ folderId: selectedFolderId, folderName: selectedFolderName },
			);

			if (!putResponse.ok) {
				let message = "Failed to link the selected folder.";
				try {
					const errorData = await putResponse.json() as { message?: string };
					if (errorData.message) {
						message = errorData.message;
					}
				} catch {
					/* JSON parse failed */
				}
				setLinkError(message);
				return;
			}

			/* Step B: trigger ingestion */
			const ingestResponse = await apiPost(
				`/api/projects/${project.id}/ingest`,
				{},
			);

			if (!ingestResponse.ok) {
				/* Folder was linked but ingestion failed — advance to Step 3
				   anyway so the user sees the sync panel. The ingestion
				   banner and auto-close mechanism will handle retries. */
			}

			/* Advance to Step 3 so the user sees sync progress.
			   onComplete() is called from Step 3 (via "Continue to Chat"
			   button or auto-close when ingestion finishes). */
			setCurrentStep(3);
		} catch {
			setLinkError("Failed to connect to the server. Please try again.");
		} finally {
			setLinking(false);
		}
	}

	/* ------------------------------------------------------------------ */
	/*  Step 3: auto-close on completion                                    */
	/* ------------------------------------------------------------------ */
	useEffect(() => {
		if (currentStep !== 3) return;
		if (autoClosedRef.current) return;

		if (
			ingestionStatus &&
			!ingestionStatus.hasPendingDocuments &&
			ingestionStatus.completed > 0
		) {
			autoClosedRef.current = true;
			onComplete();
		}
	}, [currentStep, ingestionStatus, onComplete]);

	/* ------------------------------------------------------------------ */
	/*  Step indicator                                                      */
	/* ------------------------------------------------------------------ */
	function renderStepIndicator() {
		return (
			<div className="wizard-steps">
				{STEPS.map((step, index) => {
					const state =
						step.number < currentStep
							? "done"
							: step.number === currentStep
								? "active"
								: "pending";

					return (
						<div key={step.number} className="wizard-step-item">
							<div
								className={`wizard-step wizard-step--${state}`}
								data-step={step.number}
								aria-current={state === "active" ? "step" : undefined}
							>
								<span className={`wizard-step-bubble wizard-step-bubble--${state}`}>
									{state === "done" ? (
										<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
											<polyline points="20 6 9 17 4 12" />
										</svg>
									) : (
										step.number
									)}
								</span>
								<span className="wizard-step-label">{step.label}</span>
							</div>

							{/* Connector line between steps */}
							{index < STEPS.length - 1 && (
								<div
									className={`wizard-step-connector${
										step.number < currentStep ? " wizard-step-connector--done" : ""
									}`}
								/>
							)}
						</div>
					);
				})}
			</div>
		);
	}

	/* ------------------------------------------------------------------ */
	/*  Step 1 panel                                                        */
	/* ------------------------------------------------------------------ */
	function renderStep1() {
		return (
			<div className="wizard-panel wizard-panel--active">
				<h2 className="wizard-panel-heading">Connect your project</h2>
				<p className="wizard-panel-subtext">
					Syncing tasks and milestones from Zoho Projects so PIA can understand your project context.
				</p>

				<div className="connect-status-block">
					{syncing && (
						<>
							<div className="connect-spinner" role="status" aria-label="Syncing project data">
								<span className="sr-only">Syncing…</span>
							</div>
							<p className="connect-status-text">Syncing project data…</p>
						</>
					)}

					{syncError && (
						<div className="wizard-error" role="alert">
							<div className="wizard-error-icon" aria-hidden="true">
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<circle cx="12" cy="12" r="10" />
									<line x1="12" y1="8" x2="12" y2="12" />
									<line x1="12" y1="16" x2="12.01" y2="16" />
								</svg>
							</div>
							<p className="wizard-error-text">{syncError}</p>
							<button
								type="button"
								className="wizard-retry-btn"
								onClick={() => void syncEntities()}
							>
								Retry
							</button>
						</div>
					)}

					{syncResult && !syncError && (
						<>
							<div className="connect-success-icon">
								<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
									<polyline points="20 6 9 17 4 12" />
								</svg>
							</div>
							<p className="connect-status-text">Project data synced</p>
							<div className="connect-stats">
								<span className="connect-stat-pill">
									<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
										<line x1="9" y1="12" x2="15" y2="12" />
									</svg>
									{syncResult.tasks} tasks
								</span>
								<span className="connect-stat-pill">
									<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
										<line x1="4" y1="22" x2="4" y2="15" />
									</svg>
									{syncResult.milestones} milestones
								</span>
							</div>
						</>
					)}
				</div>
			</div>
		);
	}

	/* ------------------------------------------------------------------ */
	/*  Step 2 panel                                                        */
	/* ------------------------------------------------------------------ */
	function renderStep2() {
		return (
			<div className="wizard-panel wizard-panel--active">
				<h2 className="wizard-panel-heading">Link a document folder</h2>
				<p className="wizard-panel-subtext">
					Choose a WorkDrive folder containing project documents. PIA will index these so you can ask questions about them.
				</p>

				<div className="wizard-folder-browser">
					<FolderExplorer
						projectId={project.id}
						onSelect={handleFolderSelect}
					/>
				</div>

				{linkError && (
					<div className="wizard-error wizard-error--inline" role="alert">
						<p className="wizard-error-text">{linkError}</p>
					</div>
				)}

				<button
					type="button"
					className="wizard-btn-primary"
					disabled={!selectedFolderId || linking}
					onClick={() => void handleLinkFolder()}
				>
					{linking ? (
						<>
							<span className="wizard-btn-spinner" aria-hidden="true" />
							Linking…
						</>
					) : (
						<>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
								<path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
							</svg>
							Link Selected Folder
						</>
					)}
				</button>

				<button
					type="button"
					className="wizard-btn-skip"
					onClick={onSkip}
				>
					Skip for now
				</button>
			</div>
		);
	}

	/* ------------------------------------------------------------------ */
	/*  Step 3 panel                                                        */
	/* ------------------------------------------------------------------ */
	function renderStep3() {
		const total = ingestionStatus?.total ?? 0;
		const completed = ingestionStatus?.completed ?? 0;
		const failed = ingestionStatus?.failed ?? 0;
		const isStarting = !ingestionStatus || total === 0;
		const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
		const isDone = ingestionStatus && !ingestionStatus.hasPendingDocuments && completed > 0;

		return (
			<div className="wizard-panel wizard-panel--active">
				<h2 className="wizard-panel-heading">
					{isDone ? "Ready to chat" : "Syncing documents"}
				</h2>
				<p className="wizard-panel-subtext">
					{isDone
						? `${completed} ${completed === 1 ? "document" : "documents"} indexed and ready for questions.`
						: "PIA is indexing your project documents. You can start chatting whilst this runs in the background."}
				</p>

				<div className="wizard-sync-block">
					{/* Icon: success checkmark when done, document icon otherwise */}
					{isDone ? (
						<div className="sync-file-icon sync-file-icon--done" aria-hidden="true">
							<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
								<polyline points="20 6 9 17 4 12" />
							</svg>
						</div>
					) : (
						<div className="sync-file-icon" aria-hidden="true">
							<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
								<polyline points="14 2 14 8 20 8" />
							</svg>
						</div>
					)}

					{isStarting ? (
						<p className="wizard-sync-starting">Starting sync…</p>
					) : isDone ? (
						<p className="wizard-sync-done-text" aria-live="polite">
							{failed > 0
								? `${completed} indexed, ${failed} failed`
								: `All ${completed} ${completed === 1 ? "document" : "documents"} indexed`}
						</p>
					) : (
						<div className="sync-progress-wrap">
							<div className="sync-progress-header">
								<span className="sync-progress-label">Indexing…</span>
								<span className="sync-file-counter">
									{completed} of {total} files
								</span>
							</div>
							<div
								className="sync-progress-track"
								role="progressbar"
								aria-valuenow={completed}
								aria-valuemin={0}
								aria-valuemax={Math.max(total, 1)}
								aria-label="Document sync progress"
							>
								<div
									className="sync-progress-fill"
									style={{ width: `${percentage}%` }}
								/>
							</div>
						</div>
					)}
				</div>

				<button
					type="button"
					className={`wizard-btn-primary${isDone ? " wizard-btn-primary--done" : ""}`}
					onClick={onComplete}
				>
					{isDone ? (
						<>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
								<polyline points="20 6 9 17 4 12" />
							</svg>
							Start chatting
						</>
					) : (
						"Continue to Chat"
					)}
				</button>
			</div>
		);
	}

	/* ------------------------------------------------------------------ */
	/*  Render                                                              */
	/* ------------------------------------------------------------------ */
	return (
		<div className="wizard-container">
			<div className="wizard-card">
				{renderStepIndicator()}

				{currentStep === 1 && renderStep1()}
				{currentStep === 2 && renderStep2()}
				{currentStep === 3 && renderStep3()}
			</div>
		</div>
	);
}
